import { ref, computed } from 'vue';
import { useOrganizationsStore } from '@/stores/organizations';
import type { CreateOrganizationRequest, UpdateOrganizationRequest } from '../types';

export function useOrganization(organizationId?: string) {
  const store = useOrganizationsStore();
  const internalLoading = ref(false);
  const internalError = ref<string | null>(null);

  const organization = computed(() => {
    if (organizationId) {
      if (store.currentOrganizationDetail?.id === organizationId) {
        return store.currentOrganizationDetail;
      }
      return store.organizations.find(org => org.id === organizationId) || null;
    }
    return store.currentOrganization;
  });

  const fetchOrganization = async (id: string) => {
    internalLoading.value = true;
    internalError.value = null;
    try {
      await store.fetchOrganizationDetail(id);
    } catch (err) {
      const error = err as Error;
      internalError.value = error.message || 'Failed to fetch organization';
    } finally {
      internalLoading.value = false;
    }
  };

  const domains = computed(() => store.currentDomains);

  const fetchDomains = async () => {
    const id = organizationId || store.currentOrganizationId;
    if (id) {
      await store.fetchDomains(id);
    }
  };

  const initialize = async () => {
    const id = organizationId || store.currentOrganizationId;
    if (id) {
      await fetchOrganization(id);
    }
  };

  const createOrganization = async (data: CreateOrganizationRequest) => {
    internalLoading.value = true;
    internalError.value = null;
    try {
      const newOrg = await store.createOrganization(data);
      return newOrg;
    } catch (err) {
      const error = err as Error;
      internalError.value = error.message || 'Failed to create organization';
      throw err;
    } finally {
      internalLoading.value = false;
    }
  };

  const updateOrganization = async (data: UpdateOrganizationRequest, id?: string) => {
    const targetId = id || organization.value?.id;
    if (!targetId) {
      throw new Error('Organization ID is required for update');
    }

    internalLoading.value = true;
    internalError.value = null;
    try {
      await store.updateOrganization(targetId, data);
    } catch (err) {
      const error = err as Error;
      internalError.value = error.message || 'Failed to update organization';
      throw err;
    } finally {
      internalLoading.value = false;
    }
  };

  const deleteOrganization = async (id?: string) => {
    const targetId = id || organization.value?.id;
    if (!targetId) {
      throw new Error('Organization ID is required for deletion');
    }

    internalLoading.value = true;
    internalError.value = null;
    try {
      await store.deleteOrganization(targetId);
    } catch (err) {
      const error = err as Error;
      internalError.value = error.message || 'Failed to delete organization';
      throw err;
    } finally {
      internalLoading.value = false;
    }
  };

  const addDomain = async (domain: string) => {
    const targetId = organization.value?.id;
    if (!targetId) {
      throw new Error('Organization ID is required to add domain');
    }

    internalLoading.value = true;
    internalError.value = null;
    try {
      return await store.addDomain(targetId, domain);
    } catch (err) {
      const error = err as Error;
      internalError.value = error.message || 'Failed to add domain';
      throw err;
    } finally {
      internalLoading.value = false;
    }
  };

  const verifyDomain = async (domainId: string) => {
    const targetId = organization.value?.id;
    if (!targetId) {
      throw new Error('Organization ID is required for domain verification');
    }

    internalLoading.value = true;
    internalError.value = null;
    try {
      return await store.verifyDomain(targetId, domainId);
    } catch (err) {
      const error = err as Error;
      internalError.value = error.message || 'Failed to verify domain';
      throw err;
    } finally {
      internalLoading.value = false;
    }
  };

  const removeDomain = async (domainId: string) => {
    const targetId = organization.value?.id;
    if (!targetId) {
      throw new Error('Organization ID is required to remove domain');
    }

    internalLoading.value = true;
    internalError.value = null;
    try {
      await store.removeDomain(targetId, domainId);
    } catch (err) {
      const error = err as Error;
      internalError.value = error.message || 'Failed to remove domain';
      throw err;
    } finally {
      internalLoading.value = false;
    }
  };

  return {
    organization,
    isLoading: computed(() => store.isLoading || internalLoading.value),
    error: computed(() => store.error || internalError.value),
    domains,
    initialize,
    fetchOrganization,
    fetchDomains,
    createOrganization,
    updateOrganization,
    deleteOrganization,
    addDomain,
    verifyDomain,
    removeDomain,
  };
}
